import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AddDocumentComponent } from '@/app/atoms/add-document/add-document.component';
import { WysiswygComponent } from '@/app/atoms/wysiswyg/wysiswyg.component';
import { MbaIcon } from '../../assignment/atoms/mba-icon/mba-icon';
import {
  htmlToItems,
  itemsToHtml,
  creativeText,
  plainText,
  QUALITY_CHECKS,
  QualityField,
  qualityIssues,
  sectionStatus,
} from '../briefing.helpers';
import { BriefingService } from '../briefing.service';
import { bf } from '../briefing-ui';
import { BriefingDrawer } from '../molecules/briefing-drawer/briefing-drawer';
import { BriefingListEditor } from '../molecules/briefing-list-editor/briefing-list-editor';
import { BriefingSection } from '../molecules/briefing-section/briefing-section';

/** Plain-text length at which the key message drops from the 26px display size to body size. */
const SHORT_KEY_MESSAGE = 160;

/**
 * Creative Direction: key message (the brief overview, kept as rich text), do's and don'ts
 * (edited as lists, saved back as HTML), and the brief document. Warns when the handover's
 * quality checks fail or the API won't send the brief for content review yet.
 */
@Component({
  selector: 'mbt-briefing-creative',
  imports: [BriefingSection, BriefingDrawer, BriefingListEditor, MbaIcon, WysiswygComponent, AddDocumentComponent],
  template: `
    @if (svc.brief(); as brief) {
    <mbt-briefing-section key="docs" icon="pen-line" title="Creative Direction" [flash]="flash()"
      helper="Your key message, must-haves and no-gos. Creators read this before filming, and SHOUT checks every video against it before it reaches you."
      [status]="status()" (edit)="open()">

      @if (status() === 'missing') {
        <div [class]="bf.empty">
          <span class="flex min-w-0 flex-1 flex-col gap-0.5">
            <b [class]="bf.emptyTitle">Creators don’t know what to film yet</b>
            <span [class]="bf.sub">Add a key message and the do’s every video needs, or upload your own brief document.</span>
          </span>
          <button type="button" [class]="bf.btnPrimary" (click)="open()"><mba-icon name="plus" size="size-4" />Set up</button>
        </div>
      }

      @if (status() === 'weak') {
        <div role="alert" class="flex flex-col gap-3 rounded-lg border border-[#f5d9a8] bg-[#fffaf0] p-4 dark:border-amber-500/30 dark:bg-amber-500/10">
          <div class="flex items-start gap-3">
            <span class="grid size-8 shrink-0 place-items-center rounded-full bg-[#fde7c2] text-[#b45309] dark:bg-amber-500/20 dark:text-amber-300"><mba-icon name="alert-triangle" size="size-4" /></span>
            <span class="flex flex-col gap-1">
              <b class="font-(family-name:--font-display) text-xl leading-none font-extrabold text-[#8a3b07] dark:text-amber-200">Creators Need More to Go On</b>
              <span class="text-[13px] text-[#7a4a12] dark:text-amber-100/80">Thin briefs lead to off-brief videos and more change requests. Fix {{ warnCount() === 1 ? 'this' : 'these ' + warnCount() }} before more creators are hired, or upload your own brief document.</span>
            </span>
          </div>
          <ol class="m-0 flex list-none flex-col gap-2 p-0">
            @for (issue of issues(); track $index) {
              <li class="flex items-center gap-3 rounded-md bg-(--mba-surface) px-3 py-2.5">
                <span class="flex min-w-0 flex-1 flex-col gap-0.5">
                  <b class="text-sm text-(--fg-1)">{{ issue.title }}</b>
                  <span class="text-xs text-(--fg-3)">{{ issue.detail }}</span>
                </span>
                <button type="button" [class]="bf.btnOutline" (click)="open(issue.field)">Fix</button>
              </li>
            }
            @if (!issues().length) {
              <li class="flex items-center gap-3 rounded-md bg-(--mba-surface) px-3 py-2.5">
                <span class="flex min-w-0 flex-1 flex-col gap-0.5">
                  <b class="text-sm text-(--fg-1)">SHOUT can’t review videos against this brief yet</b>
                  <span class="text-xs text-(--fg-3)">Add more detail to the key message, do’s and don’ts, or upload a brief document.</span>
                </span>
                <button type="button" [class]="bf.btnOutline" (click)="open('key')">Fix</button>
              </li>
            }
          </ol>
          <button type="button" [class]="bf.addLink" (click)="open('doc')"><mba-icon name="upload" size="size-4" />Upload a brief document instead</button>
        </div>
      }

      @if (keyHtml()) {
        <div [class]="'flex flex-col gap-2 rounded-lg bg-(--shout-deep-purple) px-[18px] py-4 text-white' + (flagged('key') ? ' ' + bf.highlight : '')">
          <span class="text-[11px] font-bold uppercase tracking-[0.08em] text-white/70">Key message · the one thing viewers should remember</span>
          <div [class]="keyShort() ? 'bf-rich font-(family-name:--font-display) text-[26px] leading-none font-bold' : 'bf-rich text-[15px] leading-[1.5]'" [innerHTML]="keyHtml()"></div>
        </div>
      }

      @if (status() !== 'missing' || dos().length || donts().length) {
        <div class="grid grid-cols-2 gap-3 max-[640px]:grid-cols-1">
          <div [class]="bf.box + ' overflow-hidden' + (flagged('dos') ? ' ' + bf.highlight : '')">
            <div class="flex items-center gap-2 bg-[rgba(11,143,78,0.08)] px-3.5 py-2.5 font-bold text-(--success)">
              <mba-icon name="check" size="size-4" [strokeWidth]="2.5" />Do’s<span class="text-xs font-medium text-(--fg-3)">Must be in every video</span>
            </div>
            <ul class="m-0 flex list-none flex-col gap-2 px-3.5 py-3">
              @for (item of dos(); track $index) {
                <li class="flex items-start gap-2 text-sm font-medium"><mba-icon name="check" size="size-4 mt-0.5 text-(--success)" />{{ item }}</li>
              } @empty {
                <li class="text-sm text-(--fg-3) italic">None added</li>
              }
            </ul>
          </div>
          <div [class]="bf.box + ' overflow-hidden' + (flagged('donts') ? ' ' + bf.highlight : '')">
            <div class="flex items-center gap-2 bg-[rgba(200,16,46,0.06)] px-3.5 py-2.5 font-bold text-(--danger)">
              <mba-icon name="x" size="size-4" [strokeWidth]="2.5" />Don’ts<span class="text-xs font-medium text-(--fg-3)">Videos get sent back</span>
            </div>
            <ul class="m-0 flex list-none flex-col gap-2 px-3.5 py-3">
              @for (item of donts(); track $index) {
                <li class="flex items-start gap-2 text-sm font-medium"><mba-icon name="x" size="size-4 mt-0.5 text-(--danger)" />{{ item }}</li>
              } @empty {
                <li class="text-sm text-(--fg-3) italic">None added</li>
              }
            </ul>
          </div>
        </div>
      }

      @if (brief.briefDocumentUrl) {
        <div class="flex items-center gap-3 rounded-lg border border-(--border-subtle) px-3.5 py-3">
          <span class="grid size-9 shrink-0 place-items-center rounded-[9px] bg-(--bg-alt) text-(--fg-2)"><mba-icon name="file-text" /></span>
          <span class="flex min-w-0 flex-1 flex-col">
            <b [class]="bf.display + ' text-lg'">Brief document</b>
            <span class="text-[13px] text-(--fg-3)">{{ documentType(brief.briefDocumentUrl) }} · creators can open it from the brief</span>
          </span>
          <a [class]="bf.link" [href]="brief.briefDocumentUrl" target="_blank" rel="noopener">Open<mba-icon name="external-link" size="size-4" /></a>
        </div>
      }

      <div [class]="bf.info">
        <mba-icon name="shield-check" size="size-4" />
        <span class="flex-1">SHOUT moderation checks every video against your do’s and don’ts before it reaches you.</span>
        <button type="button" [class]="bf.link" (click)="openEnrichments()">Moderation rules<mba-icon name="arrow-right" size="size-4" /></button>
      </div>
    </mbt-briefing-section>

    @if (editing()) {
    <mbt-briefing-drawer icon="pen-line" title="Creative Direction" [mode]="status() === 'missing' ? 'Set up' : 'Edit'"
      helper="Your key message, must-haves and no-gos. Creators read this before filming, and SHOUT checks every video against it before it reaches you."
      [saving]="saving()" [note]="svc.hired() ? svc.hired() + ' hired creators keep working from this brief' : ''"
      (closed)="editing.set(false)" (save)="save()">

      <div [class]="meterClass()">
        <div class="flex items-center gap-2">
          <b class="text-sm">{{ draftIssues().length ? 'Needs more detail' : 'Ready for creators' }}</b>
          <span class="text-xs text-(--fg-3)">{{ passed() }} of 3 checks passed</span>
        </div>
        <span class="block h-1.5 overflow-hidden rounded-full bg-(--border-subtle)">
          <i [class]="'block h-full rounded-full transition-[width] duration-[220ms] ' + (draftIssues().length ? 'bg-[#e8a54b]' : 'bg-(--success)')" [style.width.%]="(passed() / 3) * 100"></i>
        </span>
        @for (check of checks; track check.field) {
          <span class="flex items-center gap-2 text-[13px]">
            @if (issueFor(check.field); as issue) {
              <mba-icon name="circle-dashed" size="size-4 text-[#b45309] dark:text-amber-300" />{{ issue.title }}
            } @else {
              <mba-icon name="check-circle" size="size-4 text-(--success)" />{{ check.label }}
            }
          </span>
        }
      </div>

      <div [class]="bf.field">
        <span><b [class]="bf.fieldLabel">Key message</b> <span [class]="bf.fieldSub">· The one thing viewers should remember</span></span>
        <div id="bf-key" tabindex="-1" [class]="'rounded-lg' + (focusField() === 'key' ? ' ' + bf.highlight : '')">
          <atom-wysiswyg [inputHtml]="keyDraft()" placeholder="e.g. This drop brings back the classic golden-era look"
            (htmlContentChange)="keyDraft.set($event)" />
        </div>
      </div>
      <div [class]="bf.field">
        <span><b [class]="bf.fieldLabel">Do’s</b> <span [class]="bf.fieldSub">· Must be in every video</span></span>
        <mbt-briefing-list-editor label="Do" idPrefix="bf-dos" placeholder="e.g. Show the fit from the side"
          [(items)]="dosDraft" [highlight]="focusField() === 'dos'" />
      </div>
      <div [class]="bf.field">
        <span><b [class]="bf.fieldLabel">Don’ts</b> <span [class]="bf.fieldSub">· Videos that break these get sent back</span></span>
        <mbt-briefing-list-editor label="Don’t" idPrefix="bf-donts" placeholder="e.g. Mention competitor brands"
          [(items)]="dontsDraft" [highlight]="focusField() === 'donts'" />
      </div>
      <div id="bf-doc" tabindex="-1" [class]="bf.field + ' rounded-lg' + (focusField() === 'doc' ? ' ' + bf.highlight : '')">
        <span><b [class]="bf.fieldLabel">Brief document</b> <span [class]="bf.fieldSub">· Optional. PDF, DOCX or slides</span></span>
        @if (docDraft()) {
          <div [class]="bf.info"><mba-icon name="file-text" size="size-4" />
            <span class="flex-1">{{ documentType(docDraft()) }} attached. Upload another to replace it.</span>
            <a [class]="bf.link" [href]="docDraft()" target="_blank" rel="noopener">Open<mba-icon name="external-link" size="size-4" /></a>
          </div>
        }
        <atom-add-document (documentUploaded)="docDraft.set($event.fullUrl)" />
      </div>
    </mbt-briefing-drawer>
    }
    }
  `,
  styles: `
    :host ::ng-deep .bf-rich p { margin: 0; }
    :host ::ng-deep .bf-rich p + p, :host ::ng-deep .bf-rich ul, :host ::ng-deep .bf-rich ol { margin-top: 0.5em; }
    :host ::ng-deep .bf-rich ul { list-style: disc; padding-left: 1.25em; }
    :host ::ng-deep .bf-rich ol { list-style: decimal; padding-left: 1.25em; }
    :host ::ng-deep .bf-rich a { color: inherit; text-decoration: underline; }
  `,
  host: { style: 'display: contents' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BriefingCreative {
  protected readonly bf = bf;
  protected readonly checks = QUALITY_CHECKS;
  protected readonly svc = inject(BriefingService);
  private readonly router = inject(Router);

  readonly flash = input(false);

  protected readonly status = computed(() => sectionStatus('docs', this.svc.brief()!));
  protected readonly keyHtml = computed(() => this.svc.brief()?.stepsHtml ?? '');
  protected readonly keyShort = computed(() => plainText(this.keyHtml()).length <= SHORT_KEY_MESSAGE);
  protected readonly dos = computed(() => htmlToItems(this.svc.brief()?.doHtml));
  protected readonly donts = computed(() => htmlToItems(this.svc.brief()?.dontHtml));
  /** Shown only while the API says the brief isn't ready for content review (the old rule). */
  protected readonly issues = computed(() => {
    const brief = this.svc.brief();
    return brief?.isReadyForContentReview === false ? qualityIssues(creativeText(brief)) : [];
  });

  protected readonly warnCount = computed(() => this.issues().length || 1);

  protected readonly editing = signal(false);
  protected readonly saving = signal(false);
  protected readonly focusField = signal<QualityField | 'doc' | null>(null);
  protected readonly keyDraft = signal('');
  protected readonly dosDraft = signal<string[]>([]);
  protected readonly dontsDraft = signal<string[]>([]);
  protected readonly docDraft = signal('');

  protected readonly draftIssues = computed(() =>
    qualityIssues({
      key: plainText(this.keyDraft()),
      dos: plainText(itemsToHtml(this.dosDraft())),
      donts: plainText(itemsToHtml(this.dontsDraft())),
      hasDocument: !!this.docDraft().trim(),
    }),
  );
  protected readonly passed = computed(
    () => QUALITY_CHECKS.filter((check) => !this.draftIssues().some((issue) => issue.field === check.field)).length,
  );
  protected readonly meterClass = computed(() =>
    this.draftIssues().length
      ? 'flex flex-col gap-2 rounded-lg border border-[#f5d9a8] bg-[#fffaf0] p-3.5 dark:border-amber-500/30 dark:bg-amber-500/10'
      : 'flex flex-col gap-2 rounded-lg border border-[rgba(11,143,78,0.25)] bg-[rgba(11,143,78,0.06)] p-3.5',
  );

  protected flagged(field: QualityField): boolean {
    return this.issues().some((issue) => issue.field === field);
  }

  protected issueFor(field: QualityField) {
    return this.draftIssues().find((issue) => issue.field === field) ?? null;
  }

  protected documentType(url: string): string {
    const match = url.toLowerCase().match(/\.(pdf|docx?|pptx?|key|txt)(\?|$)/);
    return match ? match[1].toUpperCase() : 'Document';
  }

  open(field: QualityField | 'doc' | null = null): void {
    const brief = this.svc.brief();
    if (!brief) {
      return;
    }
    this.keyDraft.set(brief.stepsHtml ?? '');
    const dos = htmlToItems(brief.doHtml);
    const donts = htmlToItems(brief.dontHtml);
    this.dosDraft.set(dos.length ? dos : ['']);
    this.dontsDraft.set(donts.length ? donts : ['']);
    this.docDraft.set(brief.briefDocumentUrl ?? '');
    this.focusField.set(field);
    this.editing.set(true);
    if (field) {
      setTimeout(() => this.focusTarget(field));
    }
  }

  private focusTarget(field: QualityField | 'doc'): void {
    if (field === 'dos' || field === 'donts') {
      const inputs = [...document.querySelectorAll<HTMLInputElement>(`[id^="bf-${field}-"]:not([id$="-add"])`)];
      const target = inputs.find((el) => !el.value.trim()) ?? inputs[inputs.length - 1];
      (target ?? document.getElementById(`bf-${field}-add`))?.focus();
      return;
    }
    const el = document.getElementById(field === 'key' ? 'bf-key' : 'bf-doc');
    el?.scrollIntoView({ block: 'center' });
    (el?.querySelector<HTMLElement>('[contenteditable], input, textarea') ?? el)?.focus();
  }

  protected openEnrichments(): void {
    const id = this.svc.brief()?.id;
    if (id) {
      void this.router.navigate(['/brief-enhancements', id]);
    }
  }

  protected async save(): Promise<void> {
    const hasDoc = !!this.docDraft().trim();
    const dos = this.dosDraft().map((d) => d.trim()).filter(Boolean);
    if (!hasDoc && !plainText(this.keyDraft())) {
      this.svc.error('Add a key message');
      return;
    }
    if (!hasDoc && !dos.length) {
      this.svc.error('Add at least one do');
      return;
    }
    this.saving.set(true);
    const stillWeak = this.draftIssues().length > 0;
    const ok = await this.svc.save(
      [
        () =>
          this.svc.documentation({
            stepsHtml: this.keyDraft(),
            doHtml: itemsToHtml(dos),
            dontHtml: itemsToHtml(this.dontsDraft()),
            ...(hasDoc ? { briefDocumentUrl: this.docDraft().trim() } : {}),
          }),
      ],
      stillWeak ? 'Creative Direction saved · still needs more detail' : 'Creative Direction saved',
    );
    this.saving.set(false);
    if (ok) {
      this.editing.set(false);
    }
  }
}
